/**
 * Budget Calculations Utility
 * 
 * This module provides all calculation functions for budget positions and costs.
 * All calculations handle edge cases, invalid data, and maintain precision.
 * 
 * Core Formulas:
 * - Total Hours = Unit Hours × Quantity (for tracking hours worked)
 * - Total Costs = Quantity × Unit Price (for ALL cost calculations)
 * 
 * Note: Unit Hours is for tracking purposes only and does NOT affect cost calculations.
 * The Unit Price already represents the full rate per unit (hourly, monthly, etc.)
 */

import { IBudgetPosition, IBudgetCost } from '../types/studyBudget'
import { formatCurrency as formatCurrencyUtil } from './currencyFormat'
import { mult, sum, round2 } from './decimal'
import moment from 'moment'

// Type definitions for calculation results
export interface PositionCalculation {
  totalHours: number
  totalCosts: number
  isValid: boolean
  errors: string[]
}

export interface CategoryCalculation {
  category: string
  totalHours: number
  totalCosts: number
  positionCount: number
  isValid: boolean
  errors: string[]
}

export interface BudgetTotals {
  totalHours: number
  totalCosts: number
  totalPositions: number
  totalCategories: number
  isValid: boolean
  errors: string[]
}

/**
 * Validates a numeric value
 * @param value - Value to validate (must be valid number)
 * @param fieldName - Field name for error messages
 * @returns Validated number (will throw if invalid)
 * @throws Error if value is invalid
 */
function validateNumber(value: any, fieldName: string): number {
  // Fail-fast: Direct access required
  const num = typeof value === 'string' ? parseFloat(value) : Number(value)

  if (isNaN(num)) {
    throw new Error(`${fieldName} is not a valid number`)
  }

  if (!isFinite(num)) {
    throw new Error(`${fieldName} is infinite`)
  }

  if (num < 0) {
    throw new Error(`${fieldName} cannot be negative`)
  }

  return num
}

/**
 * Finds the billing rate for a specific department/position combination
 * @param billingRatesData - Array of billing rate entries from budget
 * @param departmentId - Department ID to match
 * @param positionId - Position ID to match
 * @returns The billing rate as a number, or 0 if not found
 * @example
 * const rate = getBillingRate(budget.billing_rates_data, 9, 33)
 * console.log(rate) // 195.00
 */
export function getBillingRate(
  billingRatesData: any[],
  departmentId: number,
  positionId: number
): number {
  // Fail-fast: Direct access required
  const rateEntry = billingRatesData.find((rate: any) =>
    rate.department_id === departmentId &&
    rate.position_id === positionId
  )

  // Fail-fast: Throw clear error if billing rate not found
  if (!rateEntry) {
    throw new Error(`Billing rate not found for department ${departmentId}, position ${positionId}`)
  }

  // Direct access - will throw if billing_rate missing
  const rate = typeof rateEntry.billing_rate === 'string'
    ? parseFloat(rateEntry.billing_rate)
    : rateEntry.billing_rate

  // Fail-fast: Validate the parsed rate
  if (isNaN(rate)) {
    throw new Error('Invalid billing rate')
  }

  return rate
}

/**
 * Calculates the unit price from hour_data entries using billing rates
 * @param hourData - Array of hour data entries with department/position/hours
 * @param billingRatesData - Array of billing rates from budget
 * @returns Total unit price (sum of hours × billing rate for each entry)
 * @note All calculations use decimal.js with ROUND_HALF_UP to 2 decimals
 * @example
 * const unitPrice = calculateUnitPriceFromHourData(position.hour_data, budget.billing_rates_data)
 * console.log(unitPrice) // 15600.00 (e.g., 80 hours × 195/hour)
 */
export function calculateUnitPriceFromHourData(
  hourData: any[],
  billingRatesData: any[]
): number {
  // Fail-fast: Direct calculation with decimal.js for precision
  const prices: number[] = hourData.map((entry: any) => {
    // Fail-fast: Throw if hours field is missing
    if (!('hours' in entry)) {
      throw new Error('Missing hours field in hour_data entry')
    }

    // Direct access - will crash if hours missing
    const hours = typeof entry.hours === 'string'
      ? parseFloat(entry.hours)
      : entry.hours

    // Zero hours contribute nothing; skip the rate lookup so stale orphan
    // columns (dept/position pairs no longer in billing_rates_data) don't crash
    // the page when no hours were ever entered for them.
    if (hours === 0) {
      return 0
    }

    // Get billing rate - will crash if not found
    const billingRate = getBillingRate(
      billingRatesData,
      entry.department_id,
      entry.position_id
    )

    // Calculate price for this entry using decimal.js (always rounds to 2 decimals)
    return mult(hours, billingRate)
  })

  // Sum all prices with decimal.js
  return sum(...prices)
}

/**
 * Calculates total hours from hour_data entries
 * @param hourData - Array of hour data entries
 * @returns Total hours across all entries
 * @example
 * const totalHours = calculateTotalHoursFromHourData(position.hour_data)
 * console.log(totalHours) // 160
 */
export function calculateTotalHoursFromHourData(
  hourData: any[]
): number {
  // Fail-fast: Direct calculation
  return hourData.reduce((sum: number, entry: any) => {
    // Fail-fast: Throw if hours field is missing
    if (!('hours' in entry)) {
      throw new Error('Missing hours field in hour_data entry')
    }
    const hours = typeof entry.hours === 'string'
      ? parseFloat(entry.hours)
      : entry.hours
    return sum + hours
  }, 0)
}

/**
 * Calculates total hours for a position
 * Formula: Total Hours = Unit Hours * Quantity
 */
export function calculatePositionHours(quantity: number | string, unitHours: number | string): number {
  const qty = validateNumber(quantity, 'Quantity')
  const hours = validateNumber(unitHours, 'Unit Hours')

  // Total Hours = Unit Hours * Quantity (using decimal.js)
  return mult(hours, qty)
}

/**
 * Calculates total costs for a position
 * Formula: Total Costs = Quantity × Unit Price
 * Note: Unit Price is the price per unit (e.g., hourly rate, monthly rate, etc.)
 * Unit Hours is NOT part of the cost calculation - it's only for tracking hours
 */
export function calculatePositionCosts(
  quantity: number | string,
  unitHours: number | string,
  unitPrice: number | string
): number {
  const qty = validateNumber(quantity, 'Quantity')
  const price = validateNumber(unitPrice, 'Unit Price')

  // Note: unitHours not used in cost calculation - only for tracking

  // Total Costs = Quantity × Unit Price (using decimal.js)
  return mult(qty, price)
}

/**
 * Calculates total costs for external positions (no hours involved)
 * Formula: Total Costs = Unit Price * Quantity
 */
export function calculateExternalCosts(
  quantity: number | string,
  unitPrice: number | string
): number {
  const qty = validateNumber(quantity, 'Quantity')
  const price = validateNumber(unitPrice, 'Unit Price')

  // Total Costs = Unit Price * Quantity (using decimal.js)
  return mult(price, qty)
}

/**
 * Performs full calculation for a single position
 */
export function calculatePosition(position: IBudgetPosition): PositionCalculation {
  // Fail-fast: Direct access required
  const totalHours = calculatePositionHours(position.quantity, position.hours_per_unit)
  const totalCosts = calculatePositionCosts(position.quantity, position.hours_per_unit, position.price_per_unit)

  return {
    totalHours,
    totalCosts,
    isValid: true,
    errors: []
  }
}

/**
 * Calculates totals for a category of positions
 * Only includes enabled positions (enabled !== false) to match backend behavior
 */
export function calculateCategoryTotals(
  categoryName: string,
  positions: IBudgetPosition[]
): CategoryCalculation {
  // Filter enabled positions only (matches backend: enabled !== false)
  const enabledPositions = positions.filter(p => p.enabled !== false)

  // Fail-fast: Direct calculation with decimal.js
  const hours: number[] = []
  const costs: number[] = []

  enabledPositions.forEach((position) => {
    const calc = calculatePosition(position)
    hours.push(calc.totalHours)
    costs.push(calc.totalCosts)
  })

  return {
    category: categoryName,
    totalHours: sum(...hours),
    totalCosts: sum(...costs),
    positionCount: enabledPositions.length,
    isValid: true,
    errors: []
  }
}

/**
 * Calculates grand totals for all categories
 */
export function calculateGrandTotals(categories: CategoryCalculation[]): BudgetTotals {
  // Fail-fast: Direct calculation with decimal.js
  const hours: number[] = []
  const costs: number[] = []
  let totalPositions = 0

  categories.forEach((category) => {
    hours.push(category.totalHours)
    costs.push(category.totalCosts)
    totalPositions += category.positionCount
  })

  return {
    totalHours: sum(...hours),
    totalCosts: sum(...costs),
    totalPositions,
    totalCategories: categories.length,
    isValid: true,
    errors: []
  }
}

/**
 * Calculates totals from position groups (for UI display)
 */
export function calculateGroupTotals(groups: Array<{ positions: IBudgetPosition[] }>): BudgetTotals {
  // Fail-fast: Direct calculation with decimal.js
  const hours: number[] = []
  const costs: number[] = []
  let totalPositions = 0

  groups.forEach((group) => {
    group.positions.forEach((position) => {
      const calc = calculatePosition(position)
      hours.push(calc.totalHours)
      costs.push(calc.totalCosts)
      totalPositions++
    })
  })

  return {
    totalHours: sum(...hours),
    totalCosts: sum(...costs),
    totalPositions,
    totalCategories: groups.length,
    isValid: true,
    errors: []
  }
}

/**
 * Calculates totals for external costs (no hours)
 */
export function calculateExternalCostTotals(costs: IBudgetCost[]): {
  totalCosts: number
  itemCount: number
  isValid: boolean
  errors: string[]
} {
  // Fail-fast: Direct calculation with decimal.js
  const costValues: number[] = []

  costs.forEach((cost) => {
    // Skip disabled positions
    if (cost.enabled === false) {
      return
    }

    // Skip positions with quantity = 0
    if (!cost.quantity || cost.quantity <= 0) {
      return
    }

    costValues.push(calculateExternalCosts(cost.quantity, cost.price_per_unit))
  })

  return {
    totalCosts: sum(...costValues),
    itemCount: costs.length,
    isValid: true,
    errors: []
  }
}

/**
 * Formats a number as currency using the existing formatter
 * Delegates to the formatters.ts utility which uses configured currency (JPY by default)
 */
export function formatCurrency(value: number): string {
  // Fail-fast: validate input
  if (value === null || value === undefined || isNaN(value)) {
    throw new Error('Invalid currency value')
  }
  // Direct formatting - will throw if value invalid
  return formatCurrencyUtil(value)
}

/**
 * Unit relationship types that are duration-based
 * These relationships calculate quantity based on timeline duration
 * Matches backend calculation_service.py duration constants
 */
const DURATION_BASED_RELATIONSHIPS = ['weekly', 'bi-weekly', 'monthly', 'yearly'] as const

/**
 * Checks if a unit relationship is duration-based
 * Duration-based units calculate their quantity from phase/position timeline
 * and require recalculation when dates change
 *
 * @param relationship - Unit relationship type from unit_data
 * @returns True if relationship is duration-based (weekly, bi-weekly, monthly, yearly)
 * @throws Never throws - returns false for null/undefined/unknown values
 * @example
 * isDurationBasedUnit('monthly') // returns true
 * @example
 * isDurationBasedUnit('bi-weekly') // returns true
 * @example
 * isDurationBasedUnit('related') // returns false
 * @example
 * isDurationBasedUnit('unrelated') // returns false
 * @example
 * isDurationBasedUnit('unique') // returns false
 * @example
 * isDurationBasedUnit(null) // returns false
 */
export function isDurationBasedUnit(relationship: string | null | undefined): boolean {
  if (!relationship) {
    return false
  }
  return DURATION_BASED_RELATIONSHIPS.includes(relationship as any)
}

/**
 * Calculates the duration in days between two dates (inclusive)
 * @param startDate - Start date in YYYY-MM-DD format or Date object
 * @param endDate - End date in YYYY-MM-DD format or Date object
 * @returns Number of days between dates (inclusive)
 * @throws Error if dates are invalid
 * @example
 * calculateDurationDays('2024-01-01', '2024-01-01') // returns 1
 * @example
 * calculateDurationDays('2024-01-01', '2024-01-07') // returns 7
 * @example
 * calculateDurationDays(new Date('2024-01-01'), new Date('2024-01-31')) // returns 31
 */
export function calculateDurationDays(
  startDate: string | Date,
  endDate: string | Date
): number {
  // Fail-fast: Check for null/undefined/empty values
  if (!startDate || startDate === '') {
    throw new Error('Start date is required')
  }
  if (!endDate || endDate === '') {
    throw new Error('End date is required')
  }

  // Convert to Date objects using UTC to prevent timezone shifts
  // Date-only strings (e.g., '2024-01-15') are parsed as UTC midnight
  // For Date objects, normalize to UTC midnight
  const start = startDate instanceof Date
    ? new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()))
    : new Date(startDate)
  const end = endDate instanceof Date
    ? new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()))
    : new Date(endDate)

  // Will throw if dates are invalid
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    throw new Error('Invalid date provided')
  }

  // Fail-fast: Throw if end date is before start date
  if (end.getTime() < start.getTime()) {
    throw new Error('End date cannot be before start date')
  }

  // Calculate difference in milliseconds
  const diffTime = end.getTime() - start.getTime()

  // Convert to days and add 1 (inclusive of both start and end date)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1

  return diffDays
}

/**
 * Finds the earliest start date from all phases
 * @param phases - Array of phases with start_date field
 * @returns Earliest start date in YYYY-MM-DD format
 * @throws Error if no valid start dates found
 * @example
 * const phases = [
 *   { start_date: '2024-03-01', end_date: '2024-06-01' },
 *   { start_date: '2024-01-15', end_date: '2024-05-01' }
 * ];
 * const earliestDate = getEarliestPhaseStartDate(phases);
 * // Returns: '2024-01-15'
 */
export function getEarliestPhaseStartDate(phases: any[]): string {
  const startDates = phases
    .filter(p => p.start_date && p.start_date !== '')
    .map(p => moment(p.start_date))

  if (startDates.length === 0) {
    throw new Error('No valid start dates found in phases')
  }

  return moment.min(startDates).format('YYYY-MM-DD')
}

/**
 * Finds the latest end date from all phases
 * @param phases - Array of phases with end_date field
 * @returns Latest end date in YYYY-MM-DD format
 * @throws Error if no valid end dates found
 * @example
 * const phases = [
 *   { start_date: '2024-01-01', end_date: '2024-03-31' },
 *   { start_date: '2024-02-01', end_date: '2024-06-30' }
 * ];
 * const latestDate = getLatestPhaseEndDate(phases);
 * // Returns: '2024-06-30'
 */
export function getLatestPhaseEndDate(phases: any[]): string {
  const endDates = phases
    .filter(p => p.end_date && p.end_date !== '')
    .map(p => moment(p.end_date))

  if (endDates.length === 0) {
    throw new Error('No valid end dates found in phases')
  }

  return moment.max(endDates).format('YYYY-MM-DD')
}

/**
 * Formats hours as H:MM h
 * @param decimalHours - Hours as decimal number
 * @returns Formatted hours string
 * @throws Error if hours is invalid
 * @example
 * formatHours(8.5) // returns "8:30 h"
 * formatHours(0.25) // returns "0:15 h"
 */
// Re-export formatHours from timeFormatUtils for backward compatibility
export { formatHours } from './timeFormatUtils'

/**
 * Adds numbers with precision rounding using decimal.js
 * @param numbers - Numbers to add
 * @returns Sum rounded to 2 decimal places
 * @throws Error if any number is invalid
 * @example
 * add(10.5, 20.3) // returns 30.8
 * add(0.1, 0.2) // returns 0.3 (exact, not 0.30000000000000004)
 */
export function add(...numbers: number[]): number {
  // Fail-fast: validate all inputs
  numbers.forEach(num => {
    if (num === null || num === undefined || isNaN(num)) {
      throw new Error('Invalid number in add operation')
    }
  })
  return sum(...numbers)
}

/**
 * Multiplies numbers with precision rounding using decimal.js
 * @param numbers - Numbers to multiply
 * @returns Product rounded to 2 decimal places
 * @throws Error if any number is invalid
 * @example
 * multiply(10.5, 2) // returns 21
 * multiply(0.1, 0.2) // returns 0.02 (exact, not 0.020000000004)
 */
export function multiply(...numbers: number[]): number {
  // Fail-fast: validate all inputs
  numbers.forEach(num => {
    if (num === null || num === undefined || isNaN(num)) {
      throw new Error('Invalid number in multiply operation')
    }
  })
  // Use decimal.js for precise multiplication
  return numbers.reduce((acc, num) => mult(acc, num), 1)
}

/**
 * Simple calculation helpers with clear formula order using decimal.js
 */
/**
 * Calculates direct costs after rebate
 * @param directCosts - Raw direct costs total
 * @param rebate - Rebate percentage (0-100)
 * @returns Direct costs with rebate applied
 * @example
 * calculateDirectCostsAfterRebate(100000, 10) // 90000
 */
export function calculateDirectCostsAfterRebate(directCosts: number, rebate: number): number {
  if (rebate <= 0) return directCosts
  return directCosts * (1 - rebate / 100)
}

/**
 * How an amendment's rebate, and its rebated total, differ from the source offer.
 *
 * The per-position diffs cannot express this. A rebate is a percentage, so the same rate applied
 * to a larger offer is a larger discount — an amendment that never touched the rebate still moves
 * the total by the discount on whatever was added. Leaving it out is what made the change column
 * disagree with the offer-total deviation, with nothing on screen accounting for the gap.
 *
 * `rebate` is the difference of the two DEDUCTIONS, not a deduction: 10% of 60.000 is 6.000 where
 * 10% of the source's 50.000 was 5.000, so the answer is 1.000 and not 6.000. Those two coincide
 * only when the source carried no rebate, which is exactly the case a tester builds first — see
 * the matching backend note in views_export.
 *
 * Signed as the row prints it: a deduction is negative, so a bigger discount is a negative change.
 *
 * @param directCosts - the amendment's pre-rebate direct total, live rather than saved
 * @param rebate - the amendment's rebate percentage, live
 * @param sourceDirectCosts - the source offer's pre-rebate direct total, from source_snapshot
 * @param sourceRebate - the source offer's rebate percentage, from source_snapshot
 * @returns signed deltas for the rebate row and for the rebated grand total
 * @example
 * calculateRebateDeviation(60000, 10, 50000, 10)  // { rebate: -1000, grandTotal: 9000 }
 * calculateRebateDeviation(60000, 10, 50000, 0)   // { rebate: -6000, grandTotal: 4000 }
 */
export function calculateRebateDeviation(
  directCosts: number,
  rebate: number,
  sourceDirectCosts: number,
  sourceRebate: number,
): { rebate: number; grandTotal: number } {
  const sourceAfter = calculateDirectCostsAfterRebate(sourceDirectCosts, sourceRebate)
  const liveAfter = calculateDirectCostsAfterRebate(directCosts, rebate)
  return {
    rebate: (liveAfter - directCosts) - (sourceAfter - sourceDirectCosts),
    grandTotal: liveAfter - sourceAfter,
  }
}

/**
 * Calculates budget grand total with rebate applied to direct costs
 * @param directCosts - Raw direct costs total
 * @param passThroughCosts - Pass-through costs total
 * @param rebate - Rebate percentage (0-100)
 * @returns Grand total: rebated direct costs + pass-through costs
 */
export function calculateBudgetTotalWithRebate(
  directCosts: number,
  passThroughCosts: number,
  rebate: number
): number {
  return calculateDirectCostsAfterRebate(directCosts, rebate) + passThroughCosts
}

export const BudgetFormulas = {
  /**
   * Total Hours = Unit Hours × Quantity
   * Used for tracking total hours worked/allocated
   */
  totalHours: (unitHours: number, quantity: number): number => {
    return mult(unitHours, quantity)
  },

  /**
   * Total Costs = Quantity × Unit Price
   * This is the ONLY cost calculation formula
   * Unit Price is the full rate per unit (hourly rate, monthly rate, etc.)
   */
  totalCosts: (quantity: number, unitPrice: number): number => {
    return mult(quantity, unitPrice)
  }
}